<template>
  <div>
    <v-card>
      <v-card-actions>
        <v-row>
          <v-col class="d-flex justify-space-between align-center">
            <div class="text-h6">
              Configurações do Motor de Reservas
            </div>
            <div v-if="tab === 0 && canEdit">
              <v-btn
                color="grey"
                outlined
                class="mr-2"
                :loading="loading_reset"
                :disabled="loading"
                @click="reset"
              >
                Restaurar Padrões
              </v-btn>
              <v-btn
                color="primary"
                x-large
                :loading="loading"
                :disabled="loading"
                @click="save"
              >
                Salvar
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-card-actions>
    </v-card>

    <v-tabs
      v-model="tab"
      class="mt-6"
    >
      <v-tab>Configurações Gerais</v-tab>
      <v-tab>Categorias e Tarifas</v-tab>
      <v-tab>Comodidades e Detalhes</v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab">
      <!-- Tab 1: Configurações Gerais -->
      <v-tab-item>
        <div class="mt-6">
          <!-- Configurações Gerais -->
          <v-card class="mt-6">
            <v-card-title>
              <v-icon left>
                {{ icons.mdiCog }}
              </v-icon>
              Configurações Gerais
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col
                  cols="12"
                  md="4"
                >
                  <v-switch
                    v-model="config.enabled"
                    label="Motor de Reservas Habilitado"
                    color="primary"
                    :hint="config.enabled ? 'O motor está ativo e aceitando reservas' : 'O motor está desativado'"
                    persistent-hint
                    :disabled="!canEdit"
                  ></v-switch>
                </v-col>
                <v-col
                  cols="12"
                  md="4"
                >
                  <v-switch
                    v-model="config.auto_confirm"
                    label="Confirmar Automaticamente"
                    color="primary"
                    hint="Reservas são confirmadas automaticamente sem aprovação manual"
                    persistent-hint
                    :disabled="!canEdit"
                  ></v-switch>
                </v-col>
                <v-col
                  cols="12"
                  md="4"
                >
                  <v-switch
                    v-model="config.show_prices"
                    label="Exibir Preços Publicamente"
                    color="primary"
                    hint="Mostrar valores ao público ou 'consulte'"
                    persistent-hint
                    :disabled="!canEdit"
                  ></v-switch>
                </v-col>
              </v-row>
              <v-row class="mt-4">
                <v-col
                  cols="12"
                  md="3"
                >
                  <v-switch
                    v-model="config.reserve_with_iss"
                    label="Incluir ISS nas Reservas"
                    color="primary"
                    hint="Define se reservas criadas pelo motor aplicam ISS"
                    persistent-hint
                    :disabled="!canEdit"
                  ></v-switch>
                </v-col>
                <v-col
                  cols="12"
                  md="3"
                >
                  <v-switch
                    v-model="config.analytics_enabled"
                    label="Ativar Analytics (GTM)"
                    color="primary"
                    hint="Habilita tracking e banner de consentimento no motor público"
                    persistent-hint
                    :disabled="!canEdit"
                  ></v-switch>
                </v-col>
                <v-col
                  v-if="config.analytics_enabled"
                  cols="12"
                  md="6"
                >
                  <v-text-field
                    v-model="config.gtm_container_id"
                    outlined
                    label="Container GTM"
                    hint="Exemplo: GTM-PCS26788"
                    persistent-hint
                    :disabled="!canEdit"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  cols="12"
                  md="3"
                >
                  <v-switch
                    v-model="config.associate_room"
                    label="Associar Quarto Automaticamente"
                    color="primary"
                    hint="Desative para reservar apenas a quantidade informada"
                    persistent-hint
                    :disabled="!canEdit"
                  ></v-switch>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Disponibilidade -->
          <v-card class="mt-6">
            <v-card-title>
              <v-icon left>
                {{ icons.mdiCalendarRange }}
              </v-icon>
              Disponibilidade
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col
                  cols="12"
                  md="4"
                >
                  <v-text-field
                    v-model.number="config.advance_days"
                    outlined
                    type="number"
                    label="Dias de Antecedência"
                    hint="Quantos dias no futuro aceitar reservas (ex: 365)"
                    persistent-hint
                    :rules="[v => v > 0 || 'Deve ser maior que 0']"
                  ></v-text-field>
                </v-col>
                <v-col
                  cols="12"
                  md="4"
                >
                  <v-text-field
                    v-model.number="config.min_stay"
                    outlined
                    type="number"
                    label="Mínimo de Diárias"
                    hint="Quantidade mínima de diárias"
                    persistent-hint
                    :rules="[v => v > 0 || 'Deve ser maior que 0']"
                  ></v-text-field>
                </v-col>
                <v-col
                  cols="12"
                  md="4"
                >
                  <v-text-field
                    v-model.number="config.max_stay"
                    outlined
                    type="number"
                    label="Máximo de Diárias"
                    hint="Quantidade máxima de diárias"
                    persistent-hint
                    :rules="[v => v > 0 || 'Deve ser maior que 0']"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Pagamento -->
          <v-card class="mt-6">
            <v-card-title>
              <v-icon left>
                {{ icons.mdiCash }}
              </v-icon>
              Pagamento
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="12">
                  <v-switch
                    v-model="config.require_payment"
                    label="Exigir Pagamento Antecipado"
                    color="primary"
                    hint="Reserva só é confirmada após pagamento"
                    persistent-hint
                  ></v-switch>
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  cols="12"
                  md="4"
                >
                  <v-switch
                    v-model="config.no_show_guarantee"
                    label="Garantia No-Show (1 diária)"
                    color="warning"
                    hint="Cobra o valor de uma diária como garantia"
                    persistent-hint
                    :disabled="!config.require_payment"
                  ></v-switch>
                </v-col>
                <v-col
                  cols="12"
                  md="4"
                >
                  <v-text-field
                    v-model.number="config.payment_percentage"
                    outlined
                    type="number"
                    label="Percentual do Pagamento (%)"
                    hint="Percentual do valor total para pagamento antecipado"
                    persistent-hint
                    suffix="%"
                    :disabled="!config.require_payment || config.no_show_guarantee"
                    :rules="[v => v >= 0 && v <= 100 || 'Deve estar entre 0 e 100']"
                  ></v-text-field>
                </v-col>
                <v-col
                  cols="12"
                  md="4"
                >
                  <v-text-field
                    v-model.number="config.payment_hold_minutes"
                    outlined
                    type="number"
                    label="Tempo para Concluir Pagamento (min)"
                    hint="Tempo disponível para finalizar o pagamento antes de liberar o quarto"
                    persistent-hint
                    suffix="min"
                    :disabled="!config.require_payment"
                    :rules="[v => v >= 5 && v <= 120 || 'Mantenha entre 5 e 120 minutos']"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-select
                    v-model="config.payment_methods"
                    outlined
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    label="Métodos de Pagamento Aceitos"
                    :items="paymentMethodOptions"
                    hint="Selecione os métodos de pagamento aceitos"
                    persistent-hint
                  ></v-select>
                </v-col>
              </v-row>

              <!-- Configurações de Parcelamento -->
              <v-divider class="my-4"></v-divider>
              <v-row>
                <v-col cols="12">
                  <div class="text-subtitle-1 font-weight-medium mb-2">
                    Configurações de Parcelamento (Cartão de Crédito)
                  </div>
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-text-field
                    v-model.number="config.max_installments"
                    outlined
                    type="number"
                    label="Número Máximo de Parcelas"
                    hint="Máximo de parcelas permitidas (ex: 12)"
                    persistent-hint
                    :rules="[v => v >= 1 && v <= 21 || 'Deve estar entre 1 e 21']"
                    :disabled="!config.payment_methods.includes('cartao')"
                  ></v-text-field>
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                >
                  <div class="d-flex align-center">
                    <v-text-field
                      v-model.number="config.min_installment_value"
                      outlined
                      type="number"
                      label="Valor Mínimo por Parcela (R$)"
                      hint="Deixe 0 para sem limite mínimo"
                      persistent-hint
                      prefix="R$"
                      :rules="[v => v >= 0 || 'Deve ser maior ou igual a 0']"
                      :disabled="!config.payment_methods.includes('cartao')"
                      style="flex: 1"
                    ></v-text-field>
                    <v-tooltip
                      bottom
                      max-width="400"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          color="primary"
                          class="ml-2"
                          v-bind="attrs"
                          v-on="on"
                        >
                          {{ icons.mdiInformationOutline }}
                        </v-icon>
                      </template>
                      <div>
                        <strong>Como funciona:</strong><br>
                        • Se definir R$ 0,00: sempre divide no número máximo de parcelas<br>
                        • Se definir R$ 50,00: parcelas só são permitidas se cada uma for >= R$ 50,00<br><br>
                        <strong>Exemplos com 3x máximo e R$ 50,00 mínimo:</strong><br>
                        • Compra R$ 100,00 → máximo 2x de R$ 50,00<br>
                        • Compra R$ 150,00 → até 3x de R$ 50,00<br>
                        • Compra R$ 120,00 → máximo 2x de R$ 60,00
                      </div>
                    </v-tooltip>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Cancelamento -->
          <v-card class="mt-6">
            <v-card-title>
              <v-icon left>
                {{ icons.mdiCancel }}
              </v-icon>
              Política de Cancelamento
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col
                  cols="12"
                  md="4"
                >
                  <v-text-field
                    v-model.number="config.cancellation_free_days"
                    outlined
                    type="number"
                    label="Cancelamento gratuito até"
                    hint="Clientes podem cancelar sem custos até esse número de dias antes do check-in"
                    persistent-hint
                    suffix="dias"
                    :rules="[v => v >= 0 || 'Deve ser maior ou igual a 0']"
                  ></v-text-field>
                </v-col>
                <v-col
                  cols="12"
                  md="4"
                >
                  <v-select
                    v-model="config.cancellation_early_fee_type"
                    :items="cancellationEarlyTypeOptions"
                    outlined
                    label="Cobrança até esse prazo"
                    hint="Defina se há taxa mesmo antes do limite gratuito"
                    persistent-hint
                  ></v-select>
                </v-col>
                <v-col
                  v-if="showCancellationValueField(config.cancellation_early_fee_type)"
                  cols="12"
                  md="4"
                >
                  <v-text-field
                    v-model.number="config.cancellation_early_fee_value"
                    outlined
                    :label="getCancellationValueLabel(config.cancellation_early_fee_type)"
                    :hint="getCancellationValueHint(config.cancellation_early_fee_type)"
                    persistent-hint
                    :suffix="getCancellationSuffix(config.cancellation_early_fee_type)"
                    :prefix="getCancellationPrefix(config.cancellation_early_fee_type)"
                    type="number"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-divider class="my-4"></v-divider>

              <v-row>
                <v-col
                  cols="12"
                  md="4"
                >
                  <div class="text-subtitle-2 font-weight-medium">
                    Após o prazo acima até o check-in
                  </div>
                  <p class="text-body-2 text--secondary mb-0">
                    Defina a multa aplicada quando faltarem menos dias do que o limite gratuito.
                  </p>
                </v-col>
                <v-col
                  cols="12"
                  md="4"
                >
                  <v-select
                    v-model="config.cancellation_late_fee_type"
                    :items="cancellationLateTypeOptions"
                    outlined
                    label="Tipo de cobrança"
                    persistent-hint
                  ></v-select>
                </v-col>
                <v-col
                  v-if="showCancellationValueField(config.cancellation_late_fee_type)"
                  cols="12"
                  md="4"
                >
                  <v-text-field
                    v-model.number="config.cancellation_late_fee_value"
                    outlined
                    :label="getCancellationValueLabel(config.cancellation_late_fee_type)"
                    :hint="getCancellationValueHint(config.cancellation_late_fee_type)"
                    persistent-hint
                    :suffix="getCancellationSuffix(config.cancellation_late_fee_type)"
                    :prefix="getCancellationPrefix(config.cancellation_late_fee_type)"
                    type="number"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-divider class="my-4"></v-divider>

              <v-row>
                <v-col
                  cols="12"
                  md="4"
                >
                  <div class="text-subtitle-2 font-weight-medium">
                    No-show / Cancelamentos no dia
                  </div>
                  <p class="text-body-2 text--secondary mb-0">
                    Penalidade aplicada quando o hóspede não comparece ou cancela no dia do check-in.
                  </p>
                </v-col>
                <v-col
                  cols="12"
                  md="4"
                >
                  <v-select
                    v-model="config.cancellation_noshow_fee_type"
                    :items="cancellationNoshowTypeOptions"
                    outlined
                    label="Tipo de cobrança"
                    persistent-hint
                  ></v-select>
                </v-col>
                <v-col
                  v-if="showCancellationValueField(config.cancellation_noshow_fee_type)"
                  cols="12"
                  md="4"
                >
                  <v-text-field
                    v-model.number="config.cancellation_noshow_fee_value"
                    outlined
                    :label="getCancellationValueLabel(config.cancellation_noshow_fee_type)"
                    :hint="getCancellationValueHint(config.cancellation_noshow_fee_type)"
                    persistent-hint
                    :suffix="getCancellationSuffix(config.cancellation_noshow_fee_type)"
                    :prefix="getCancellationPrefix(config.cancellation_noshow_fee_type)"
                    type="number"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Políticas de Grupo (temporariamente oculto a pedido do cliente)
    <v-card class="mt-6">
      <v-card-title>
        <v-icon left>{{ icons.mdiAccountGroup }}</v-icon>
        Reservas em Grupo
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <v-switch
              v-model="config.allow_group"
              label="Permitir Reservas em Grupo"
              color="primary"
              hint="Aceitar reservas para grupos"
              persistent-hint
            ></v-switch>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model.number="config.group_min_rooms"
              outlined
              type="number"
              label="Mínimo de Quartos para Grupo"
              hint="Quantidade mínima de quartos para ser considerado grupo"
              persistent-hint
              :disabled="!config.allow_group"
              :rules="[v => v > 0 || 'Deve ser maior que 0']"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    -->

          <!-- Políticas de Crianças -->
          <v-card class="mt-6">
            <v-card-title>
              <v-icon left>
                {{ icons.mdiBabyFace }}
              </v-icon>
              Política para Crianças
            </v-card-title>
            <v-card-text>
              <p class="text-body-2 text--secondary mb-4">
                Faixas isentas não geram cobrança; faixas parciais usam o adicional definido em cada categoria; faixas integrais contam como adulto para formar o preço.
              </p>
              <div
                v-for="(policy, index) in config.child_policies"
                :key="policy.id || `child-policy-${index}`"
                class="child-policy-row"
              >
                <v-text-field
                  v-model.number="policy.min_age"
                  outlined
                  dense
                  type="number"
                  min="0"
                  label="Idade inicial"
                  :disabled="!canEdit"
                  hide-details="auto"
                ></v-text-field>
                <v-text-field
                  v-if="index === config.child_policies.length - 1"
                  value="Em diante"
                  outlined
                  dense
                  label="Idade final"
                  readonly
                  disabled
                  hide-details="auto"
                ></v-text-field>
                <v-text-field
                  v-else
                  v-model.number="policy.max_age"
                  outlined
                  dense
                  type="number"
                  min="0"
                  label="Idade final"
                  :disabled="!canEdit"
                  hide-details="auto"
                ></v-text-field>
                <v-select
                  v-model="policy.charge_type"
                  :items="childChargeTypeOptions"
                  outlined
                  dense
                  label="Tipo de cobrança"
                  :disabled="!canEdit"
                  hide-details="auto"
                ></v-select>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      color="error"
                      :disabled="!canEdit || config.child_policies.length === 1"
                      aria-label="Remover faixa de idade"
                      v-bind="attrs"
                      v-on="on"
                      @click="removeChildPolicy(index)"
                    >
                      <v-icon>{{ icons.mdiDeleteOutline }}</v-icon>
                    </v-btn>
                  </template>
                  <span>Remover faixa</span>
                </v-tooltip>
              </div>
              <v-alert
                v-if="childPolicyError"
                type="error"
                text
                dense
                role="alert"
                class="mt-3 mb-2"
              >
                {{ childPolicyError }}
              </v-alert>
              <v-btn
                text
                color="primary"
                class="mt-2"
                :disabled="!canEdit"
                @click="addChildPolicy"
              >
                <v-icon
                  left
                  small
                >
                  {{ icons.mdiPlus }}
                </v-icon>
                Adicionar faixa
              </v-btn>
            </v-card-text>
          </v-card>

          <!-- Notificações -->
          <v-card class="mt-6">
            <v-card-title>
              <v-icon left>
                {{ icons.mdiBell }}
              </v-icon>
              Notificações
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-switch
                    v-model="config.send_confirmation_email"
                    label="Enviar E-mail de Confirmação"
                    color="primary"
                    hint="Enviar e-mail automático ao confirmar reserva"
                    persistent-hint
                  ></v-switch>
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-switch
                    v-model="config.send_confirmation_whatsapp"
                    label="Enviar WhatsApp de Confirmação"
                    color="primary"
                    hint="Enviar mensagem WhatsApp ao confirmar reserva"
                    persistent-hint
                  ></v-switch>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-textarea
                    v-model="config.booking_terms"
                    outlined
                    rows="4"
                    label="Termos e Condições"
                    hint="Texto exibido aos clientes ao fazer reserva"
                    persistent-hint
                  ></v-textarea>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </div>
      </v-tab-item>

      <!-- Tab 2: Categorias e Tarifas -->
      <v-tab-item>
        <div class="mt-6">
          <v-card>
            <v-card-title>
              <v-icon left>
                {{ icons.mdiDoor }}
              </v-icon>
              Configuração de Tarifas por Categoria
            </v-card-title>
            <v-card-text>
              <v-alert
                type="info"
                text
                class="mb-4"
              >
                Configure as tarifas e disponibilidade para cada categoria de quarto. Apenas categorias habilitadas aparecerão no motor de reservas público.
              </v-alert>

              <v-data-table
                :headers="categoryHeaders"
                :items="categories"
                :loading="loadingCategories"
                class="elevation-1"
              >
                <template v-slot:item.enabled="{ item }">
                  <v-switch
                    v-model="item.config.enabled"
                    color="success"
                    dense
                    hide-details
                  ></v-switch>
                </template>

                <template v-slot:item.tariff_type="{ item }">
                  <v-select
                    v-model="item.config.tariff_type"
                    :items="[{text: 'Manual', value: 'manual'}, {text: 'Tarifa do Sistema', value: 'system'}]"
                    dense
                    hide-details
                    :disabled="!item.config.enabled"
                  ></v-select>
                </template>

                <template v-slot:item.price="{ item }">
                  <div
                    v-if="item.config.tariff_type === 'manual'"
                    class="d-flex align-center"
                  >
                    <span class="text-body-2 mr-2">{{ getManualPricingLabel(item) }}</span>
                    <v-tooltip top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          small
                          color="primary"
                          :disabled="!item.config.enabled || !canEdit"
                          aria-label="Configurar preço manual"
                          v-bind="attrs"
                          v-on="on"
                          @click="openManualPricing(item)"
                        >
                          <v-icon small>
                            {{ icons.mdiTuneVariant }}
                          </v-icon>
                        </v-btn>
                      </template>
                      <span>Configurar preço manual</span>
                    </v-tooltip>
                  </div>
                  <div v-else-if="item.config.tariff_type === 'system'">
                    <v-select
                      v-model="item.config.rate_plan_id"
                      :items="ratePlans"
                      item-text="description"
                      item-value="id"
                      dense
                      hide-details
                      clearable
                      placeholder="Selecione o tarifário"
                      :disabled="!item.config.enabled || !canEdit"
                    ></v-select>
                  </div>
                </template>

                <template v-slot:item.hotel_reserved="{ item }">
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model.number="item.config.hotel_reserved_quantity"
                        type="number"
                        dense
                        hide-details
                        :disabled="!item.config.enabled"
                        placeholder="0"
                        min="0"
                        :max="item.total_rooms"
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <span>Quantidade bloqueada para uso interno do hotel (não aparece no motor)</span>
                  </v-tooltip>
                </template>

                <template v-slot:item.max_rooms="{ item }">
                  <v-text-field
                    v-model.number="item.config.max_rooms_available"
                    type="number"
                    dense
                    hide-details
                    :disabled="!item.config.enabled"
                    placeholder="Todos"
                    min="0"
                  ></v-text-field>
                </template>

                <template v-slot:item.available_info="{ item }">
                  <v-chip
                    small
                    :color="calculateAvailable(item) > 0 ? 'success' : 'error'"
                    text-color="white"
                  >
                    {{ calculateAvailable(item) }} / {{ item.total_rooms }}
                  </v-chip>
                </template>

                <template v-slot:item.display_order="{ item }">
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model.number="item.config.display_order"
                        type="number"
                        dense
                        hide-details
                        :disabled="!item.config.enabled"
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <span>Define a ordem de exibição: números menores aparecem primeiro</span>
                  </v-tooltip>
                </template>

                <template v-slot:item.current_price="{ item }">
                  <span class="font-weight-bold">{{ getCurrentPriceLabel(item) }}</span>
                </template>

                <template v-slot:item.special_rates="{ item }">
                  <v-btn
                    color="secondary"
                    x-small
                    :disabled="!canEdit"
                    @click="openSpecialRatesModal(item)"
                  >
                    Tarifa/Período
                  </v-btn>
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-btn
                    color="primary"
                    small
                    :loading="item.saving"
                    :disabled="!canEdit"
                    @click="updateCategory(item)"
                  >
                    Salvar
                  </v-btn>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </div>
      </v-tab-item>

      <!-- Tab 3: Comodidades e Detalhes -->
      <v-tab-item>
        <amenities-config-tab :can-edit="canEdit" />
      </v-tab-item>
    </v-tabs-items>

    <special-rates-modal
      v-model="specialRatesDialog"
      :category="selectedCategoryRates"
      :can-edit="canEdit"
      :child-policies="config.child_policies"
      @closed="handleSpecialRatesModalClosed"
      @updated="loadRoomCategories"
    />
    <manual-pricing-dialog
      v-model="manualPricingDialog"
      :category="selectedManualCategory"
      :child-policies="config.child_policies"
      @apply="applyManualPricing"
    />
  </div>
</template>

<script>
import {
  mdiAccountGroup,
  mdiBabyFace,
  mdiBell,
  mdiCalendarRange,
  mdiCancel,
  mdiCash,
  mdiCog,
  mdiDoor,
  mdiInformationOutline,
  mdiDeleteOutline,
  mdiPlus,
  mdiTuneVariant,
} from '@mdi/js'
import { mapActions, mapGetters } from 'vuex'
import SpecialRatesModal from './_components/SpecialRatesModal.vue'
import AmenitiesConfigTab from './_components/AmenitiesConfigTab.vue'
import ManualPricingDialog from './_components/ManualPricingDialog.vue'

export default {
  name: 'BookingEngineConfig',
  components: {
    SpecialRatesModal,
    AmenitiesConfigTab,
    ManualPricingDialog,
  },
  data: () => ({
    tab: 0,
    loading_reset: false,
    loadingCategories: false,
    categories: [],
    tariffs: [],
    ratePlans: [],
    permissions: JSON.parse(localStorage.getItem('permissions')) || [],
    specialRatesDialog: false,
    selectedCategoryRates: {
      id: null,
      name: '',
    },
    manualPricingDialog: false,
    selectedManualCategory: null,
    childPolicyError: '',
    icons: {
      mdiAccountGroup,
      mdiBabyFace,
      mdiBell,
      mdiCalendarRange,
      mdiCancel,
      mdiCash,
      mdiCog,
      mdiDoor,
      mdiInformationOutline,
      mdiDeleteOutline,
      mdiPlus,
      mdiTuneVariant,
    },
    categoryHeaders: [
      { text: 'Categoria', value: 'name', sortable: false },
      {
        text: 'Habilitado', value: 'enabled', sortable: false, width: '100px',
      },
      {
        text: 'Tipo Tarifa', value: 'tariff_type', sortable: false, width: '160px',
      },
      {
        text: 'Preço', value: 'price', sortable: false, width: '170px',
      },
      {
        text: 'Preço Atual', value: 'current_price', sortable: false, width: '120px',
      },
      {
        text: 'Tarifa/Período', value: 'special_rates', sortable: false, width: '140px',
      },
      {
        text: 'Reserva Hotel', value: 'hotel_reserved', sortable: false, width: '140px',
      },
      {
        text: 'Máx Motor', value: 'max_rooms', sortable: false, width: '140px',
      },
      {
        text: 'Disponível', value: 'available_info', sortable: false, width: '120px',
      },
      {
        text: 'Ordem', value: 'display_order', sortable: false, width: '100px',
      },
      {
        text: 'Ações', value: 'actions', sortable: false, width: '120px', align: 'center',
      },
    ],
    config: {
      // Configurações Gerais
      enabled: true,
      auto_confirm: true,
      show_prices: true,
      analytics_enabled: false,
      gtm_container_id: null,

      // Disponibilidade
      advance_days: 365,
      min_stay: 1,
      max_stay: 30,

      // Pagamento
      require_payment: false,
      payment_percentage: 30.00,
      no_show_guarantee: false,
      reserve_with_iss: true,
      associate_room: true,
      payment_methods: [],
      max_installments: 12,
      min_installment_value: 0.00,
      payment_hold_minutes: 20,

      // Cancelamento
      cancellation_hours: 24,
      cancellation_fee: 0.00,
      cancellation_fee_percentage: 0.00,
      cancellation_free_days: 7,
      cancellation_early_fee_type: 'none',
      cancellation_early_fee_value: 0,
      cancellation_late_fee_type: 'daily',
      cancellation_late_fee_value: 1,
      cancellation_noshow_fee_type: 'percentage',
      cancellation_noshow_fee_value: 100,

      // Políticas de Grupo
      allow_group: true,
      group_min_rooms: 5,

      // Políticas de Crianças
      child_policies: [{ min_age: 0, max_age: null, charge_type: 'full' }],

      // Notificações
      send_confirmation_email: true,
      send_confirmation_whatsapp: false,
      booking_terms: null,
    },
    paymentMethodOptions: [
      { text: 'PIX', value: 'pix' },
      { text: 'Cartão de Crédito', value: 'cartao' },
      { text: 'Cartão de Débito', value: 'debito' },
      { text: 'Dinheiro', value: 'dinheiro' },
      { text: 'Boleto', value: 'boleto' },
      { text: 'Transferência', value: 'transferencia' },
    ],
    cancellationEarlyTypeOptions: [
      { text: 'Grátis', value: 'none' },
      { text: 'Percentual (%)', value: 'percentage' },
      { text: 'Valor fixo (R$)', value: 'fixed' },
    ],
    cancellationLateTypeOptions: [
      { text: 'Percentual (%)', value: 'percentage' },
      { text: 'Diárias', value: 'daily' },
      { text: 'Valor fixo (R$)', value: 'fixed' },
    ],
    cancellationNoshowTypeOptions: [
      { text: 'Integral (100%)', value: 'total' },
      { text: 'Percentual (%)', value: 'percentage' },
      { text: 'Diárias', value: 'daily' },
      { text: 'Valor fixo (R$)', value: 'fixed' },
    ],
    childChargeTypeOptions: [
      { text: 'Isenta', value: 'free' },
      { text: 'Parcial', value: 'partial' },
      { text: 'Integral', value: 'full' },
    ],
  }),

  computed: {
    ...mapGetters('bookingEngineConfig', {
      storeConfig: 'getConfig',
      loading: 'isLoading',
    }),
    canView() {
      return this.hasPermission('system.booking_engine', 'index')
             || this.hasPermission('system.booking_engine', 'getRoomCategories')
             || this.hasPermission('system.booking_engine', 'status')
    },
    canEdit() {
      return this.hasPermission('system.booking_engine', 'update')
             || this.hasPermission('system.booking_engine', 'reset')
             || this.hasPermission('system.booking_engine', 'updateRoomCategory')
    },
  },

  watch: {
    storeConfig: {
      handler(newConfig) {
        if (newConfig) {
          this.config = { ...this.config, ...newConfig }
          if (!Array.isArray(this.config.child_policies) || !this.config.child_policies.length) {
            this.config.child_policies = [{ min_age: 0, max_age: null, charge_type: 'full' }]
          }
        }
      },
      immediate: true,
    },
    tab(newTab) {
      if (newTab === 1) {
        this.loadRoomCategories()
      }
    },
    'config.require_payment': function (value) {
      if (!value) {
        this.config.no_show_guarantee = false
      }
    },
    'config.cancellation_early_fee_type': function (value) {
      this.handleCancellationTypeChange('cancellation_early_fee', value)
    },
    'config.cancellation_late_fee_type': function (value) {
      this.handleCancellationTypeChange('cancellation_late_fee', value)
    },
    'config.cancellation_noshow_fee_type': function (value) {
      this.handleCancellationTypeChange('cancellation_noshow_fee', value)
    },
    specialRatesDialog(value) {
      if (!value) {
        this.selectedCategoryRates = { id: null, name: '' }
      }
    },
  },

  mounted() {
    this.loadConfig()
    this.loadRatePlanOptions()
  },

  methods: {
    ...mapActions('bookingEngineConfig', ['loadConfig', 'updateConfig', 'resetConfig']),
    ...mapActions('rate_plan', { loadRatePlans: 'getForSelect' }),

    hasPermission(dir, action) {
      if (!dir || !action) return true

      return this.permissions.some(p => p.dir === dir && p.action === action)
    },

    async loadRoomCategories() {
      this.loadingCategories = true
      try {
        const response = await this.$http.get('system/booking-engine-config/room-categories')
        if (response.data.success) {
          this.categories = response.data.data.categories.map(cat => ({
            ...cat,
            saving: false, // Estado de salvamento
            config: cat.config || {
              enabled: false,
              tariff_type: 'system',
              manual_price: null,
              tariff_id: null,
              rate_plan_id: null,
              max_rooms_available: null,
              hotel_reserved_quantity: 0,
              display_order: 0,
              price: 0,
            },
          })).map(cat => ({
            ...cat,
            config: {
              manual_pricing_mode: 'flat',
              manual_occupancy_rates: [],
              manual_child_rates: [],
              ...cat.config,
            },
          }))
          this.tariffs = response.data.data.tariffs || []
        }
      } catch (error) {
        console.error('Erro ao carregar categorias:', error)
        this.$toast.error('Erro ao carregar categorias')
      } finally {
        this.loadingCategories = false
      }
    },

    async loadRatePlanOptions() {
      try {
        const res = await this.loadRatePlans()
        this.ratePlans = res.data || []
      } catch (error) {
        console.error('Erro ao carregar tarifarios:', error)
        this.$toast.error('Erro ao carregar tarifarios')
      }
    },

    openSpecialRatesModal(category) {
      if (!category || !category.id) {
        return
      }
      const isManual = category.config.tariff_type === 'manual'
      const ratePlanItem = isManual ? null : this.getRatePlanItemForCategory(category)
      this.selectedCategoryRates = {
        id: category.id,
        name: category.name,
        config: category.config,
        base_price: isManual
          ? category.config.manual_price
          : ratePlanItem?.value,
        pricing_mode: isManual
          ? category.config.manual_pricing_mode || 'flat'
          : ratePlanItem?.pricing_mode || 'flat',
        occupancy_rates: isManual
          ? category.config.manual_occupancy_rates || []
          : ratePlanItem?.occupancy_rates || [],
        child_rates: isManual
          ? category.config.manual_child_rates || []
          : ratePlanItem?.child_rates || [],
      }
      this.specialRatesDialog = true
    },

    handleSpecialRatesModalClosed() {
      this.specialRatesDialog = false
      this.selectedCategoryRates = { id: null, name: '' }
    },

    openManualPricing(category) {
      this.selectedManualCategory = category
      this.manualPricingDialog = true
    },

    async applyManualPricing(config) {
      if (!this.selectedManualCategory) return
      this.selectedManualCategory.config = config
      await this.updateCategory(this.selectedManualCategory)
    },

    getManualPricingLabel(category) {
      if (category.config.manual_pricing_mode === 'occupancy') {
        const count = (category.config.manual_occupancy_rates || []).length

        return `${count} ${count === 1 ? 'variação' : 'variações'}`
      }

      return `R$ ${this.formatPrice(category.config.manual_price)}`
    },

    async addChildPolicy() {
      const policies = this.config.child_policies
      const previous = policies[policies.length - 1]
      if (!previous) {
        policies.push({ min_age: 0, max_age: null, charge_type: 'full' })

        return
      }

      const suggestedAge = previous.max_age === null || previous.max_age === undefined || previous.max_age === ''
        ? Number(previous.min_age) + 1
        : Number(previous.max_age) + 1
      const result = await this.$swal.fire({
        title: 'Início da nova faixa',
        text: `A faixa atual será encerrada em um ano antes. Exemplo: começando em ${suggestedAge}, a faixa anterior terminará em ${suggestedAge - 1}.`,
        input: 'number',
        inputValue: suggestedAge,
        inputAttributes: {
          min: Number(previous.min_age) + 1,
          step: 1,
        },
        showCancelButton: true,
        confirmButtonText: 'Adicionar faixa',
        cancelButtonText: 'Cancelar',
        inputValidator: value => {
          const age = Number(value)
          if (!Number.isInteger(age) || age <= Number(previous.min_age)) {
            return `Informe uma idade inteira maior que ${previous.min_age}.`
          }

          return null
        },
      })

      if (!result.isConfirmed) return

      const minAge = Number(result.value)
      previous.max_age = minAge - 1
      policies.push({ min_age: minAge, max_age: null, charge_type: 'full' })
      this.childPolicyError = ''
    },

    removeChildPolicy(index) {
      this.config.child_policies.splice(index, 1)
      const last = this.config.child_policies[this.config.child_policies.length - 1]
      if (last) last.max_age = null
      this.childPolicyError = ''
    },

    validateChildPolicies() {
      const policies = this.config.child_policies || []
      if (!policies.length) return 'Adicione ao menos uma faixa de idade.'
      for (let index = 0; index < policies.length; index += 1) {
        const policy = policies[index]
        const minAge = Number(policy.min_age)
        const expectedMin = index === 0 ? 0 : Number(policies[index - 1].max_age) + 1
        const isLast = index === policies.length - 1
        if (!Number.isInteger(minAge) || minAge < 0) return 'As idades devem ser números inteiros maiores ou iguais a zero.'
        if (minAge !== expectedMin) return `A faixa ${index + 1} deve iniciar em ${expectedMin} anos para manter a continuidade.`
        if (isLast && policy.max_age !== null && policy.max_age !== undefined && policy.max_age !== '') return 'A última faixa deve ficar aberta (idade final vazia).'
        if (!isLast) {
          const maxAge = Number(policy.max_age)
          if (!Number.isInteger(maxAge) || maxAge < minAge) return `Informe uma idade final válida para a faixa ${index + 1}.`
        }
      }

      return ''
    },

    async updateCategory(category) {
      const isEnabled = category.config.enabled

      if (isEnabled) {
        if (category.config.tariff_type === 'manual'
          && category.config.manual_pricing_mode !== 'occupancy'
          && !category.config.manual_price) {
          this.$toast.error('Informe o preço manual')

          return
        }

        if (category.config.tariff_type === 'manual'
          && category.config.manual_pricing_mode === 'occupancy'
          && !(category.config.manual_occupancy_rates || []).length) {
          this.$toast.error('Configure ao menos uma variação de ocupação')

          return
        }

        if (category.config.tariff_type === 'system' && !category.config.rate_plan_id && !category.config.tariff_id) {
          this.$toast.error('Selecione o tarifario para esta categoria')

          return
        }

        if (category.config.tariff_type === 'system') {
          const ratePlanValue = this.getRatePlanDailyPriceForCategory(category)
          if (ratePlanValue === null || Number(ratePlanValue) <= 0) {
            this.$toast.error('Tarifario com valor 0,00 para esta categoria. Ajuste o tarifario ou use Manual.')

            return
          }
        }

        if (category.config.max_rooms_available && category.config.max_rooms_available > category.total_rooms) {
          this.$toast.error(`A quantidade disponível não pode exceder ${category.total_rooms} quartos`)

          return
        }

        if (category.config.hotel_reserved_quantity && category.config.hotel_reserved_quantity > category.total_rooms) {
          this.$toast.error(`A reserva do hotel não pode exceder ${category.total_rooms} quartos`)

          return
        }

        const available = this.calculateAvailable(category)
        if (available <= 0) {
          this.$toast.warning('Atenção: Nenhum quarto disponível para o motor de reservas com esta configuração')
        }
      }

      this.$set(category, 'saving', true)
      try {
        const response = await this.$http.post(
          `system/booking-engine-config/room-categories/${category.id}`,
          category.config,
        )
        if (response.data.success) {
          this.$toast.success('Categoria atualizada com sucesso!')

          // Recarregar dados para atualizar o preço atual
          await this.loadRoomCategories()
        }
      } catch (error) {
        console.error('Erro ao atualizar categoria:', error)
        const errorMsg = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'Erro ao atualizar categoria'
        this.$toast.error(errorMsg)
      } finally {
        this.$set(category, 'saving', false)
      }
    },

    formatPrice(price) {
      return parseFloat(price || 0).toFixed(2)
    },

    getSelectedRatePlan(category) {
      const selectedId = Number(category?.config?.rate_plan_id || 0)
      if (!selectedId) return null

      return this.ratePlans.find(plan => Number(plan.id) === selectedId) || null
    },

    getRatePlanItemForCategory(category) {
      const ratePlan = this.getSelectedRatePlan(category)
      if (!ratePlan || !Array.isArray(ratePlan.items)) return null

      return ratePlan.items.find(item => Number(item.room_category_id) === Number(category.id)) || null
    },

    isPositivePrice(value) {
      const number = Number(value)

      return Number.isFinite(number) && number > 0
    },

    getRatePlanDailyPriceForCategory(category) {
      const item = this.getRatePlanItemForCategory(category)
      if (!item) return null

      if (item.pricing_mode === 'occupancy') {
        const rates = Array.isArray(item.occupancy_rates) ? item.occupancy_rates : []
        const today = new Date()
        const isWeekend = today.getDay() === 0 || today.getDay() === 6
        const values = rates.map(rate => {
          if (isWeekend && this.isPositivePrice(rate.weekend_value)) return Number(rate.weekend_value)

          return this.isPositivePrice(rate.value) ? Number(rate.value) : null
        }).filter(value => value !== null)

        return values.length ? Math.min(...values) : null
      }

      const today = new Date()
      const yyyy = today.getFullYear()
      const mm = String(today.getMonth() + 1).padStart(2, '0')
      const dd = String(today.getDate()).padStart(2, '0')
      const todayKey = `${yyyy}-${mm}-${dd}`

      const specialRates = Array.isArray(item.special_rates) ? item.special_rates : []
      const special = specialRates.find(rate => rate.date === todayKey)
      if (special && this.isPositivePrice(special.value)) {
        return Number(special.value)
      }

      const isWeekend = today.getDay() === 0 || today.getDay() === 6
      const weekendValue = Number(item.weekend_value)
      const baseValue = Number(item.value)

      if (isWeekend && this.isPositivePrice(weekendValue)) {
        return weekendValue
      }

      if (this.isPositivePrice(baseValue)) {
        return baseValue
      }

      return null
    },

    getCurrentPriceLabel(category) {
      if (category.config.tariff_type !== 'system' || !category.config.rate_plan_id) {
        return `R$ ${this.formatPrice(category.config.price)}`
      }

      const value = this.getRatePlanDailyPriceForCategory(category)
      if (value === null) {
        return 'R$ 0,00'
      }

      return `R$ ${this.formatPrice(value)}`
    },

    getRatePlanPricePreview(category) {
      const ratePlan = this.getSelectedRatePlan(category)
      const value = this.getRatePlanDailyPriceForCategory(category)

      if (!ratePlan) return 'Selecione um tarifario'
      if (value === null) return `${ratePlan.description} - R$ 0,00`

      return `${ratePlan.description} - R$ ${this.formatPrice(value)}`
    },

    calculateAvailable(item) {
      const total = item.total_rooms || 0
      const reserved = item.config.hotel_reserved_quantity || 0
      const maxRooms = item.config.max_rooms_available

      // Disponível após deduzir a reserva do hotel
      const availableAfterReserve = Math.max(0, total - reserved)

      // Se há limite máximo configurado, aplica o menor valor
      if (maxRooms !== null && maxRooms !== undefined && maxRooms !== '') {
        return Math.min(availableAfterReserve, maxRooms)
      }

      return availableAfterReserve
    },

    showCancellationValueField(type) {
      return type && !['none', 'total'].includes(type)
    },

    getCancellationValueLabel(type) {
      switch (type) {
        case 'percentage':
          return 'Percentual cobrado'
        case 'daily':
          return 'Quantidade de diárias'
        case 'fixed':
          return 'Valor fixo'
        default:
          return 'Valor'
      }
    },

    getCancellationValueHint(type) {
      switch (type) {
        case 'percentage':
          return 'Informe um percentual entre 0 e 100%'
        case 'daily':
          return 'Quantidade de diárias cobradas (ex: 1 = uma diária)'
        case 'fixed':
          return 'Valor fixo em reais'
        default:
          return ''
      }
    },

    getCancellationSuffix(type) {
      if (type === 'percentage') return '%'
      if (type === 'daily') return 'diárias'

      return ''
    },

    getCancellationPrefix(type) {
      return type === 'fixed' ? 'R$' : ''
    },

    handleCancellationTypeChange(prefix, type) {
      const key = `${prefix}_value`
      if (!this.config || !(key in this.config)) {
        return
      }

      if (type === 'none') {
        this.config[key] = 0

        return
      }

      if (type === 'total') {
        this.config[key] = 100

        return
      }

      if (!this.config[key] || this.config[key] <= 0) {
        this.config[key] = type === 'daily' ? 1 : 50
      }
    },

    validateCancellationValue(type, value, message) {
      if (!type || ['none', 'total'].includes(type)) {
        return true
      }

      if (value === null || value === undefined || value === '') {
        this.$toast.error(`${message}: informe o valor da cobrança`)

        return false
      }

      if (type === 'percentage' && (value < 0 || value > 100)) {
        this.$toast.error(`${message}: o percentual deve estar entre 0 e 100%`)

        return false
      }

      if (type === 'daily' && (value <= 0 || value > 365)) {
        this.$toast.error(`${message}: informe a quantidade de diárias (mínimo 1)`)

        return false
      }

      if (type === 'fixed' && value < 0) {
        this.$toast.error(`${message}: o valor deve ser maior ou igual a 0`)

        return false
      }

      return true
    },

    save() {
      if (!this.validate()) {
        return
      }

      this.updateConfig(this.config)
        .then(response => {
          if (response.data.success) {
            this.$toast.success('Configurações salvas com sucesso!')
          }
        })
        .catch(error => {
          console.error('Erro ao salvar:', error)
          if (error.response && error.response.data && error.response.data.message) {
            this.$toast.error(error.response.data.message)
          } else {
            this.$toast.error('Erro ao salvar configurações')
          }
        })
    },

    reset() {
      if (!window.confirm('Tem certeza que deseja restaurar as configurações padrão? Esta ação não pode ser desfeita.')) {
        return
      }

      this.loading_reset = true

      this.resetConfig()
        .then(response => {
          if (response.data.success) {
            this.$toast.success('Configurações restauradas com sucesso!')
          }
        })
        .catch(error => {
          console.error('Erro ao resetar:', error)
          this.$toast.error('Erro ao restaurar configurações')
        })
        .finally(() => {
          this.loading_reset = false
        })
    },

    validate() {
      // Validar min_stay < max_stay
      if (this.config.min_stay > this.config.max_stay) {
        this.$toast.error('O mínimo de diárias não pode ser maior que o máximo')

        return false
      }

      // Validar advance_days
      if (this.config.advance_days <= 0) {
        this.$toast.error('Dias de antecedência deve ser maior que 0')

        return false
      }

      // Validar payment_percentage
      if (this.config.require_payment && !this.config.no_show_guarantee) {
        if (this.config.payment_percentage < 0 || this.config.payment_percentage > 100) {
          this.$toast.error('Percentual de pagamento deve estar entre 0 e 100')

          return false
        }
      }

      if (this.config.cancellation_free_days < 0) {
        this.$toast.error('Dias para cancelamento gratuito deve ser maior ou igual a 0')

        return false
      }

      if (
        !this.validateCancellationValue(
          this.config.cancellation_early_fee_type,
          this.config.cancellation_early_fee_value,
          'Cancelamento antecipado',
        )
      ) {
        return false
      }

      if (
        !this.validateCancellationValue(
          this.config.cancellation_late_fee_type,
          this.config.cancellation_late_fee_value,
          'Cancelamentos próximos ao check-in',
        )
      ) {
        return false
      }

      if (
        !this.validateCancellationValue(
          this.config.cancellation_noshow_fee_type,
          this.config.cancellation_noshow_fee_value,
          'No-show',
        )
      ) {
        return false
      }

      if (this.config.require_payment) {
        if (!this.config.payment_hold_minutes || this.config.payment_hold_minutes < 5 || this.config.payment_hold_minutes > 120) {
          this.$toast.error('O tempo para concluir o pagamento deve ficar entre 5 e 120 minutos')

          return false
        }
      }

      // Validar cancellation_fee_percentage
      if (this.config.cancellation_fee_percentage < 0 || this.config.cancellation_fee_percentage > 100) {
        this.$toast.error('Percentual de taxa de cancelamento deve estar entre 0 e 100')

        return false
      }

      this.childPolicyError = this.validateChildPolicies()
      if (this.childPolicyError) {
        this.$toast.error(this.childPolicyError)

        return false
      }

      if (this.config.analytics_enabled) {
        const gtmId = String(this.config.gtm_container_id || '').trim()
        if (!gtmId) {
          this.$toast.error('Informe o Container GTM para ativar o analytics')

          return false
        }
        if (!/^GTM-[A-Z0-9]+$/.test(gtmId)) {
          this.$toast.error('Container GTM inválido. Use o formato GTM-XXXXXXX')

          return false
        }
      }

      return true
    },
  },
}
</script>

<style scoped>
.child-policy-row {
  display: grid;
  grid-template-columns: minmax(130px, 1fr) minmax(130px, 1fr) minmax(170px, 1.4fr) 44px;
  gap: 12px;
  align-items: start;
  margin-bottom: 12px;
}

@media (max-width: 700px) {
  .child-policy-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    padding: 12px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 8px;
  }
}
</style>
